import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { apiOrigin } from '@/utils/apiBase';

const API = apiOrigin();
const REFRESH = 60000;

const formatINR = (n) =>
  '₹' + (Number(n || 0)).toLocaleString('en-IN', { maximumFractionDigits: 0 });

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso.includes('T') ? iso : iso + 'T00:00:00');
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso.slice(0, 10); }
};

/**
 * Public, read-only Money sheet for an event.
 *
 * Designed to be shared via WhatsApp / email with co-hosts, spouse, accountant.
 * Uses a LIGHT theme on purpose — dark themes don't render in WhatsApp link
 * previews / email clients and many recipients won't be on dark mode.
 */
export default function PublicMoneySheetPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState('summary');
  const [locked, setLocked] = useState(null);   // {name, share_id} when password required
  const [pwInput, setPwInput] = useState('');
  const [pwError, setPwError] = useState('');
  const [pwUnlocked, setPwUnlocked] = useState('');  // remembered password for refreshes

  const fetchSheet = async (passwordOverride) => {
    const pw = passwordOverride !== undefined ? passwordOverride : pwUnlocked;
    try {
      const headers = pw ? { 'X-Sheet-Password': pw } : {};
      const r = await axios.get(`${API}/api/events/public/money-sheet/${token}`, { headers });
      if (r.data?.locked) {
        setLocked({ name: r.data.name, share_id: r.data.share_id });
        setData(null);
      } else {
        setLocked(null);
        setData(r.data);
        setErr('');
      }
    } catch (e) {
      if (e.response?.status === 401) {
        setPwError('Incorrect password. Try again.');
        // keep the lock screen visible; do NOT clear `locked`
      } else if (e.response?.status === 410) {
        setErr('⏰ This share link has expired. Ask the event host for a fresh one.');
      } else {
        setErr(e.response?.status === 404 ? 'This link has expired or been revoked.' : 'Could not load sheet.');
      }
    }
  };

  useEffect(() => {
    fetchSheet();
    const id = setInterval(() => fetchSheet(), REFRESH);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [token, pwUnlocked]);

  // Force light theme on this page
  useEffect(() => {
    const root = document.documentElement;
    const prevDark = root.classList.contains('dark');
    root.classList.remove('dark');
    root.classList.add('light');
    return () => {
      if (prevDark) { root.classList.add('dark'); root.classList.remove('light'); }
    };
  }, []);

  const handleUnlock = (e) => {
    if (e?.preventDefault) e.preventDefault();
    setPwError('');
    setPwUnlocked(pwInput);  // triggers refetch via effect
  };

  if (err) return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[#FAFAF7] text-neutral-900">
      <div className="text-center">
        <div className="text-5xl mb-3">🔒</div>
        <p className="text-base text-neutral-700">{err}</p>
        <p className="text-xs text-neutral-500 mt-2">Ask the event host for a fresh link.</p>
      </div>
    </div>
  );

  if (locked) return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[#FAFAF7] text-neutral-900"
      data-testid="public-sheet-locked">
      <form onSubmit={handleUnlock}
        className="w-full max-w-sm rounded-3xl p-7 text-center"
        style={{ background: 'white', border: '1px solid rgba(0,0,0,0.08)', boxShadow: '0 8px 24px rgba(0,0,0,0.08)' }}>
        <div className="text-5xl mb-2">🔒</div>
        <h1 className="text-lg font-extrabold mb-1">{locked.name}</h1>
        <p className="text-xs text-neutral-500 mb-5">This sheet is password-protected. Ask the host for the password.</p>
        <input type="password" value={pwInput} onChange={e => setPwInput(e.target.value)}
          data-testid="sheet-password-input"
          placeholder="Enter password"
          autoFocus
          className="w-full px-4 py-3 rounded-xl border border-neutral-200 text-sm outline-none focus:border-[#FF6B35] focus:ring-2 focus:ring-[#FF6B35]/20" />
        {pwError && <p className="text-xs text-red-600 mt-2 text-left">{pwError}</p>}
        <button type="submit"
          data-testid="sheet-unlock-btn"
          className="w-full mt-3 px-4 py-3 rounded-xl bg-[#FF6B35] text-white text-sm font-extrabold hover:opacity-90">
          Unlock
        </button>
      </form>
    </div>
  );

  if (!data) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAF7]">
      <div className="w-7 h-7 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const themeColor = data.event.theme_color || '#FF6B35';
  const t = data.totals;
  const netProfit = t.net_cost < 0;
  const v = data.visibility || {
    show_budget: true, show_expenses: true, show_shagun: true,
    show_summary: true, show_variation: true,
  };

  // Tabs available based on visibility
  const tabs = [];
  if (v.show_summary) tabs.push({ id: 'summary', label: 'Summary' });
  if (v.show_budget) tabs.push({ id: 'budget', label: `Budget (${data.budget_lines.length})` });
  if (v.show_expenses) tabs.push({ id: 'expenses', label: `Expenses (${data.expenses.length})` });
  if (v.show_shagun) tabs.push({ id: 'shagun', label: `Shagun (${data.shagun.length})` });

  // If currently selected tab is now hidden, fall back to first available
  if (tabs.length && !tabs.find(x => x.id === tab) && tabs[0]) {
    setTimeout(() => setTab(tabs[0].id), 0);
  }

  return (
    <div className="min-h-screen text-neutral-900" data-testid="public-money-sheet"
      style={{ background: '#FAFAF7' }}>
      {/* Header */}
      <div className="border-b border-black/5 px-5 py-5 sheet-header"
        style={{ background: `linear-gradient(135deg, ${themeColor}18 0%, transparent 65%), white` }}>
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3">
            <span className="text-4xl">{data.event.theme_emoji}</span>
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl font-extrabold leading-tight" style={{ color: themeColor }}>
                {data.event.title}
              </h1>
              <p className="text-xs text-neutral-700 mt-0.5">
                {fmtDate(data.event.event_date)}{data.event.venue ? ` · ${data.event.venue}` : ''}
              </p>
            </div>
            <button onClick={() => window.print()} data-testid="sheet-print-btn" title="Print or save as PDF"
              className="hidden sm:flex items-center gap-1.5 px-3 py-2 rounded-full bg-white border border-black/10 text-xs font-bold text-neutral-700 hover:bg-neutral-50 shadow-sm print-hide">
              🖨 Print / PDF
            </button>
          </div>
          {/* Mobile print btn */}
          <button onClick={() => window.print()}
            className="sm:hidden mt-3 w-full px-3 py-2 rounded-xl bg-white border border-black/10 text-xs font-bold text-neutral-700 hover:bg-neutral-50 shadow-sm print-hide">
            🖨 Print / Save as PDF
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-5 py-5 space-y-4">
        {/* Totals */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
          <Stat label="Planned" value={formatINR(t.planned)} color="#525252" />
          <Stat label="Advance" value={formatINR(t.advance_paid ?? t.actual_spent)} color="#DC2626" />
          <Stat label="Shagun" value={formatINR(t.shagun_received)} color="#16A34A" />
          <Stat label={netProfit ? 'Net Profit 🎉' : 'Net Cost'}
            value={formatINR(Math.abs(t.net_cost))}
            color={netProfit ? '#16A34A' : themeColor} bold />
        </div>

        {/* Settlement row (only if visibility allows AND at least one line is settled) */}
        {t.any_final_set && v.show_variation && (
          <div className="grid grid-cols-3 gap-2.5">
            <Stat label="Final" value={formatINR(t.final_total)} color="#D97706" />
            <Stat label="Balance Due"
              value={formatINR(Math.abs(t.balance_due))}
              color={t.balance_due > 0 ? '#DC2626' : '#16A34A'} />
            <Stat label="Variation"
              value={(t.variation === 0 ? '₹0' : (t.variation > 0 ? '+' : '-') + formatINR(Math.abs(t.variation)))}
              color={t.variation > 0 ? '#DC2626' : t.variation < 0 ? '#16A34A' : '#525252'} />
          </div>
        )}

        {/* Tabs */}
        {tabs.length > 1 && (
        <div className="flex gap-1 p-1 rounded-2xl w-fit overflow-x-auto print-hide"
          style={{ background: 'white', border: '1px solid rgba(0,0,0,0.08)', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          {tabs.map(s => (
            <button key={s.id} onClick={() => setTab(s.id)}
              data-testid={`sheet-tab-${s.id}`}
              className={`px-3 sm:px-4 py-2 rounded-xl text-xs font-bold transition whitespace-nowrap ${tab === s.id ? '' : 'text-neutral-600 hover:text-neutral-900 hover:bg-black/5'}`}
              style={tab === s.id ? { background: `${themeColor}1A`, color: themeColor } : {}}>
              {s.label}
            </button>
          ))}
        </div>
        )}

        {v.show_summary && (
          <div className={`space-y-3 print-section ${tab === 'summary' ? '' : 'hidden print-show'}`}>
            <Card title="Per-category breakdown">
              {data.category_breakdown.length === 0 && (
                <p className="text-xs text-neutral-500 py-3">Nothing to show yet.</p>
              )}
              {data.category_breakdown.map(c => {
                const pct = c.planned > 0 ? Math.min(100, Math.round(c.actual * 100 / c.planned)) : 0;
                const over = c.actual > c.planned && c.planned > 0;
                return (
                  <div key={c.category} className="py-2 border-b border-black/5 last:border-0">
                    <div className="flex items-center justify-between text-xs mb-1.5">
                      <span className="text-neutral-900 font-semibold">{c.category}</span>
                      <span>
                        <span className="text-neutral-500">{formatINR(c.planned)}</span>
                        {' · '}
                        <span className={over ? 'text-red-600 font-bold' : 'text-neutral-900 font-bold'}>{formatINR(c.actual)}</span>
                      </span>
                    </div>
                    {c.planned > 0 && (
                      <div className="w-full h-1.5 rounded-full bg-black/5 overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: over ? '#DC2626' : pct > 80 ? '#F59E0B' : '#16A34A' }} />
                      </div>
                    )}
                  </div>
                );
              })}
            </Card>
            {data.relation_breakdown.length > 0 && (
              <Card title="Shagun by relation">
                {data.relation_breakdown.map(r => (
                  <div key={r.relation} className="flex items-center justify-between py-1.5 text-sm border-b border-black/5 last:border-0">
                    <span className="text-neutral-900 font-medium">{r.relation}</span>
                    <span className="font-bold text-green-700">{formatINR(r.amount)}</span>
                  </div>
                ))}
              </Card>
            )}
          </div>
        )}

        {v.show_budget && (
          <div className={`print-section ${tab === 'budget' ? '' : 'hidden print-show'}`}>
          <Card title="Budget lines">
            {data.budget_lines.length === 0 && <p className="text-xs text-neutral-500 py-3">No budget lines yet.</p>}
            {data.budget_lines.map(line => {
              const hasFinal = line.has_final;
              const denom = hasFinal ? Math.max(line.planned_amount, line.final_amount || 0) : line.planned_amount;
              const pct = denom > 0 ? Math.min(100, Math.round(line.advance_paid * 100 / denom)) : 0;
              const overBudget = hasFinal && (line.final_amount || 0) > line.planned_amount;
              return (
                <div key={line.line_id} className="py-3 border-b border-black/5 last:border-0">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-neutral-900">{line.description || line.category}</p>
                      <p className="text-[10px] text-neutral-500 uppercase tracking-widest mt-0.5 font-semibold">{line.category}</p>
                    </div>
                    {hasFinal ? (
                      <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded"
                        style={{ background: overBudget ? '#FEE2E2' : '#DCFCE7', color: overBudget ? '#991B1B' : '#166534' }}>
                        {overBudget ? 'OVER' : 'SETTLED'}
                      </span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded bg-neutral-100 text-neutral-600">
                        Open
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5 text-[11px]">
                    <PublicPill label="Budget" value={formatINR(line.planned_amount)} />
                    <PublicPill label="Advance" value={formatINR(line.advance_paid)} tone="green" />
                    <PublicPill label="Final" value={hasFinal ? formatINR(line.final_amount) : '—'} tone="amber" muted={!hasFinal} />
                    <PublicPill label="Balance"
                      value={hasFinal ? formatINR(Math.abs(line.balance)) : '—'}
                      tone={line.balance > 0 ? 'red' : line.balance < 0 ? 'green' : 'neutral'}
                      muted={!hasFinal} />
                    <PublicPill label="Var"
                      value={hasFinal ? ((line.variation > 0 ? '+' : line.variation < 0 ? '-' : '') + formatINR(Math.abs(line.variation))) : '—'}
                      tone={line.variation > 0 ? 'red' : line.variation < 0 ? 'green' : 'neutral'}
                      muted={!hasFinal} />
                  </div>
                  {denom > 0 && (
                    <div className="w-full h-1.5 rounded-full bg-black/5 overflow-hidden mt-2">
                      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: overBudget ? '#DC2626' : pct >= 100 ? '#16A34A' : pct > 80 ? '#F59E0B' : '#16A34A' }} />
                    </div>
                  )}
                </div>
              );
            })}
          </Card>
          </div>
        )}

        {v.show_expenses && (
          <div className={`print-section ${tab === 'expenses' ? '' : 'hidden print-show'}`}>
          <Card title={`Expenses · ${formatINR(t.actual_spent)}`}>
            {data.expenses.length === 0 && <p className="text-xs text-neutral-500 py-3">No expenses booked yet.</p>}
            {data.expenses.map((e, i) => (
              <div key={i} className="py-2 border-b border-black/5 last:border-0 flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-neutral-500">{fmtDate(e.date)} · {e.payment_mode}</p>
                  <p className="text-sm font-semibold text-neutral-900 truncate">{e.description || e.category}</p>
                  <p className="text-[10px] text-neutral-500 uppercase tracking-widest mt-0.5">{e.category}</p>
                </div>
                <div className="text-sm font-bold text-red-600">-{formatINR(e.amount)}</div>
              </div>
            ))}
          </Card>
          </div>
        )}

        {v.show_shagun && (
          <div className={`print-section ${tab === 'shagun' ? '' : 'hidden print-show'}`}>
          <Card title={`Shagun received · ${formatINR(t.shagun_received)}`}>
            {data.shagun.length === 0 && <p className="text-xs text-neutral-500 py-3">No shagun recorded yet.</p>}
            {data.shagun.map(s => (
              <div key={s.shagun_id} className="py-2 border-b border-black/5 last:border-0 flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-neutral-900">{s.giver_name}</p>
                  <p className="text-[10px] text-neutral-500 uppercase tracking-widest mt-0.5 font-semibold">{s.relation} · {s.payment_mode}</p>
                  {s.note && <p className="text-xs text-neutral-600 mt-1">{s.note}</p>}
                </div>
                <div className="text-sm font-bold text-green-700">+{formatINR(s.amount)}</div>
              </div>
            ))}
          </Card>
          </div>
        )}

        <div className="text-center text-[10px] text-neutral-500 pt-4 print-hide">
          Auto-refreshes every minute · {data.expenses.length + data.budget_lines.length + data.shagun.length} total records
        </div>
      </div>

      {/* Print styles — show all sections, hide UI chrome */}
      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          html, body { background: white !important; }
          .print-hide { display: none !important; }
          .print-section.hidden { display: block !important; }
          .print-show { display: block !important; }
          .sheet-header {
            border-bottom: 1px solid #000 !important;
            background: white !important;
          }
          [data-testid="public-money-sheet"] {
            background: white !important;
          }
          .print-section { page-break-inside: avoid; margin-top: 8mm; }
        }
      `}</style>
    </div>
  );
}

function Stat({ label, value, color, bold }) {
  return (
    <div className="rounded-2xl p-3" style={{ background: 'white', border: '1px solid rgba(0,0,0,0.08)', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">{label}</div>
      <div className={`mt-1 ${bold ? 'text-lg' : 'text-base'} font-extrabold`} style={{ color }}>{value}</div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="rounded-2xl p-4" style={{ background: 'white', border: '1px solid rgba(0,0,0,0.08)', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2 font-bold">{title}</div>
      {children}
    </div>
  );
}

function PublicPill({ label, value, tone = 'neutral', muted = false }) {
  const toneMap = {
    neutral: { bg: '#F5F5F5', color: '#404040' },
    green:   { bg: '#DCFCE7', color: '#15803D' },
    amber:   { bg: '#FEF3C7', color: '#B45309' },
    red:     { bg: '#FEE2E2', color: '#B91C1C' },
  };
  const tn = toneMap[tone] || toneMap.neutral;
  return (
    <div className="rounded-md px-1.5 py-1 text-center" style={{ background: muted ? '#FAFAFA' : tn.bg }}>
      <div className="text-[8px] uppercase tracking-wider font-bold" style={{ color: muted ? '#A3A3A3' : tn.color, opacity: 0.7 }}>{label}</div>
      <div className="text-[11px] font-bold leading-tight mt-0.5" style={{ color: muted ? '#A3A3A3' : tn.color }}>{value}</div>
    </div>
  );
}
